import { FilingList } from "components/HomeActionItems/FilingList";
import { Form1099Actions } from "components/HomeActionItems/Form1099Actions";
import { InvoicesList } from "components/HomeActionItems/InvoicesList";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import {
  type PendingSectionKey,
  type TaxActionItemType,
} from "store/apis/actionItems";
import { openLink } from "utils/openLink";
import { CartTaskModal } from "./CartTaskModal";
import { PendingTaskItemsModal } from "./PendingTaskItemsModal";
import { PendingTaskRowShell } from "./PendingTaskRowShell";
import {
  type PendingAccent,
  type PendingBehavior,
} from "./pendingSectionConfig";
import { type TagColorType } from "components/DesignSystem/Tag/Tag";

export type PendingRow = {
  key: PendingSectionKey;
  rowId: string;
  behavior: PendingBehavior;
  accent: PendingAccent;
  title: string;
  subtitle: string;
  actionLabel: string;
  tag?: { label: string; tone: TagColorType };
  ids: string[];
  entityId: string | null;
  stateId: string | null;
  count: number;
};

const buildTaxActionItem = (
  row: PendingRow,
  customType: TaxActionItemType["custom_type"]
): TaxActionItemType => ({
  count: row.count,
  custom_type: customType,
  title: row.title,
  ids: row.ids,
  entity_id: row.entityId ?? "",
  order: 0,
  subtitle: row.subtitle,
});

export const PendingTaskRow = ({ row }: { row: PendingRow }) => {
  const history = useHistory();
  const { isAdmin } = useRoleBasedView();
  const { uuid: groupId } = useCurrentGroupContext();

  const [isOpen, setIsOpen] = useState(false);

  const closeModal = () => setIsOpen(false);

  const redirectToStateCompliance = () => {
    const entitySuffix = row.entityId ? `?entity=${row.entityId}` : "";
    if (row.entityId && row.stateId) {
      const path = isAdmin
        ? `/admin/crm/${groupId}/state-compliance/${row.stateId}?entity=${row.entityId}&openMissingInfo=true`
        : `/tax/state-compliance/${row.stateId}?entity=${row.entityId}&openMissingInfo=true`;
      history.push(path);
      return;
    }
    const path = isAdmin
      ? `/admin/crm/${groupId}/state-compliance${entitySuffix}`
      : `/tax/state-compliance${entitySuffix}`;
    history.push(path);
  };

  const handleAction = () => {
    if (row.behavior === "STATE_COMPLIANCE") {
      redirectToStateCompliance();
      return;
    }

    if (row.behavior === "W_FORMS" && row.ids.length === 1) {
      openLink(`/tax/filings/${row.ids[0]}`);
      return;
    }

    setIsOpen(true);
  };

  return (
    <>
      <PendingTaskRowShell
        accent={row.accent}
        title={row.title}
        subtitle={row.subtitle}
        actionLabel={row.actionLabel}
        tag={row.tag}
        onAction={handleAction}
      />

      {row.behavior === "MESSAGE" && (
        <PendingTaskItemsModal
          open={isOpen}
          title={row.title}
          ids={row.ids}
          onClose={closeModal}
        />
      )}

      {row.behavior === "CART" && (
        <CartTaskModal
          open={isOpen}
          entityIds={row.ids}
          groupId={groupId}
          onClose={closeModal}
        />
      )}

      {row.behavior === "INVOICES" && row.entityId && (
        <InvoicesList
          isOpen={isOpen}
          close={closeModal}
          entityId={row.entityId}
        />
      )}

      {row.behavior === "FILING" && (
        <FilingList
          item={buildTaxActionItem(row, "FILING_IN_ADD_DETAILS_CUSTOM_TYPE")}
          open={isOpen}
          onOpenChange={setIsOpen}
          hideTrigger
        />
      )}

      {row.behavior === "W_FORMS" && row.ids.length > 1 && (
        <Form1099Actions
          item={buildTaxActionItem(row, "W_FORMS_PENDING")}
          groupId={groupId}
          open={isOpen}
          onOpenChange={setIsOpen}
          hideTrigger
        />
      )}
    </>
  );
};
